import { useCallback, useEffect, useRef, useState } from "react";
import apiClient from "../api/apiClient";

const PAGE_SIZE = 20;

/**
 * Listing endpoint of a Discover / My mockups feed, or null when the feed
 * cannot load yet (e.g. Same creator before a mockup is open).
 *   suggested  published marketplace listings
 *   mine       your own and purchased listings (root, or one of your folders)
 *   creator    listings of the active mockup's creator
 */
export function feedUrl({ tab, search, creatorId, folderId }, page) {
  const params = new URLSearchParams({ page: String(page), page_size: String(PAGE_SIZE) });
  if (search) params.set("search", search);
  if (tab === "suggested") params.set("called_from", "home");
  else if (tab === "mine") {
    if (folderId) params.set("folder", String(folderId));
    else params.set("mockup", "mockup");
  } else if (tab === "creator") {
    if (!creatorId) return null;
    params.set("user_id", String(creatorId));
  } else return null;
  return `/listings/?${params.toString()}`;
}

/**
 * Paginated listings for a feed, loaded page by page (infinite scroll).
 * `filter` drops entries client-side (e.g. unpublished ones).
 */
export function useListingFeed({ tab, search = "", creatorId = null, folderId = null, enabled = true, filter }) {
  const [state, setState] = useState({ items: [], loading: false, hasMore: true, error: null, page: 0 });
  const requestRef = useRef(0);
  const filterRef = useRef(filter);
  filterRef.current = filter;
  const key = JSON.stringify([tab, search, creatorId, folderId]);
  const stateRef = useRef(state);
  stateRef.current = state;

  const load = useCallback(
    async (page) => {
      const url = feedUrl({ tab, search, creatorId, folderId }, page);
      if (!url) {
        setState({ items: [], loading: false, hasMore: false, error: null, page: 0 });
        return;
      }
      const request = ++requestRef.current;
      setState((s) => ({ ...s, loading: true, error: null }));
      const result = await apiClient.get(url);
      if (request !== requestRef.current) return; // superseded
      if (!result.ok) {
        setState((s) => ({ ...s, loading: false, hasMore: false, error: "Could not load mockups." }));
        return;
      }
      const rows = Array.isArray(result.data) ? result.data : result.data?.results || [];
      const kept = filterRef.current ? rows.filter(filterRef.current) : rows;
      setState((s) => {
        const seen = new Set(page === 1 ? [] : s.items.map((l) => l.id));
        const items = page === 1 ? [] : s.items.slice();
        for (const row of kept) if (!seen.has(row.id)) items.push(row), seen.add(row.id);
        return { items, loading: false, hasMore: Boolean(result.data?.next), error: null, page };
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key],
  );

  useEffect(() => {
    if (!enabled) return;
    setState({ items: [], loading: true, hasMore: true, error: null, page: 0 });
    load(1);
  }, [load, enabled]);

  const loadMore = useCallback(() => {
    const s = stateRef.current;
    if (!enabled || s.loading || !s.hasMore || s.error) return;
    load(s.page + 1);
  }, [enabled, load]);

  const reload = useCallback(() => load(1), [load]);

  /** Updates one listing in place (e.g. after a purchase). */
  const patch = useCallback((id, changes) => setState((s) => ({ ...s, items: s.items.map((l) => (l.id === id ? { ...l, ...changes } : l)) })), []);

  return { ...state, loadMore, reload, patch };
}

/** Calls `onVisible` whenever the sentinel element scrolls into view. */
export function useInfiniteSentinel(onVisible, deps = []) {
  const ref = useRef(null);
  const callback = useRef(onVisible);
  callback.current = onVisible;
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver((entries) => entries.some((e) => e.isIntersecting) && callback.current(), { rootMargin: "300px 0px" });
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return ref;
}
